import { FC } from 'react';

import { Box, Center, Image, Spinner, Text, VStack } from '@bonusly/design-system';

import { SpotlightCardEmbed } from '../../spotlight';
import { useEmployeeSpotlightSelectedSection } from '../../spotlight/hooks/use-employee-spotlight-selected-section';
import { EmployeeSpotlightAttachment } from '../lib/entities/attachment';

type EmployeeSpotlightActionMediaProps = {
	attachment: EmployeeSpotlightAttachment;
};

export const EmployeeSpotlightActionMedia: FC<EmployeeSpotlightActionMediaProps> = ({ attachment }) => {
	const spotlightId = attachment.spotlightId || '';
	const sectionId = attachment.data.employee_spotlight_section_id;
	const isSingleCardShare = Boolean(sectionId);

	const { isLoading, selectedSection } = useEmployeeSpotlightSelectedSection({
		spotlightId,
		sectionId,
		enabled: isSingleCardShare && Boolean(spotlightId),
	});

	if (isLoading) {
		return (
			<Box
				bg="bg.surface"
				borderRadius="lg"
				border="1px solid"
				borderColor="border.default"
				overflow="hidden"
				p={3}
				pb={4}
				data-testid="employee-spotlight-attachment"
			>
				<Center py={8} data-testid="employee-spotlight-loading">
					<VStack spacing={3}>
						<Spinner size="lg" color="primary.fg" />
					</VStack>
				</Center>
			</Box>
		);
	}

	if (isSingleCardShare && selectedSection) {
		return (
			<Box data-testid="employee-spotlight-attachment">
				<SpotlightCardEmbed section={selectedSection} />
			</Box>
		);
	}

	if (!isSingleCardShare && attachment.imageUrl) {
		const aspectRatio = attachment.width != null && attachment.height != null ? `${attachment.width} / ${attachment.height}` : undefined;
		return (
			<Box
				bg="bg.surface"
				borderRadius="lg"
				border="1px solid"
				borderColor="border.default"
				overflow="hidden"
				p={3}
				pb={4}
				data-testid="employee-spotlight-attachment"
			>
				<Image
					src={attachment.imageUrl}
					width="100%"
					aspectRatio={aspectRatio}
					borderRadius="md"
					alt={attachment.altText}
					data-testid="employee-spotlight-image"
				/>
				{attachment.title && (
					<Text mt={4} fontWeight="semibold" fontSize="md" color="text.default" data-testid="employee-spotlight-title">
						{attachment.title}
					</Text>
				)}
			</Box>
		);
	}

	return (
		<Box
			bg="bg.surface"
			borderRadius="lg"
			border="1px solid"
			borderColor="border.default"
			overflow="hidden"
			p={3}
			pb={4}
			data-testid="employee-spotlight-attachment"
		>
			<Center py={8} data-testid="employee-spotlight-empty">
				<Text fontSize="sm" color="text.soft" textAlign="center">
					{attachment.title || 'Spotlight'}
				</Text>
			</Center>
		</Box>
	);
};
